import { ImportFilePayload } from "@/common/utils/types";
import { headerMappingConfig } from "./ImportModal";
import { useState, useEffect } from "react";
import Papa from "papaparse";
import ArrowRightIcon from "@icons/arrow-right.svg?react";
import HDSingleSelect from "../HDSingleSelect";
import classNames from "classnames";
import { getImportMapping } from "@/common/lib/supabaseClient";

interface MatcherProps {
    parsedCsv: Papa.ParseResult<unknown> | undefined;
    mapping: Partial<Record<keyof ImportFilePayload, number>>;
    setMapping: React.Dispatch<React.SetStateAction<Partial<Record<keyof ImportFilePayload, number>>>>;
}

const Matcher: React.FC<MatcherProps> = ({ parsedCsv, mapping, setMapping }) => {
    const [error, setError] = useState<string | null>(null);

    // get the headers from the csv
    const csvHeaders = parsedCsv?.meta.fields || [];

    // handle mapping change
    const handleMappingChange = (key: keyof ImportFilePayload, value: string) => {
        // clear the error if there is one
        if (error) {
            setError(null);
        }

        // if the user clears the selection, set the mapping to undefined
        if (!value) {
            setMapping((prev) => {
                const newMapping = { ...prev };
                delete newMapping[key];
                return newMapping;
            });
            return;
        }

        // if the user selects the same value unselect it by deleting that key
        if (mapping[key] === csvHeaders.indexOf(value)) {
            setMapping((prev) => {
                const newMapping = { ...prev };
                delete newMapping[key];
                return newMapping;
            });
            return;
        }

        // get the index of the selected value
        const columnIndex = csvHeaders.indexOf(value);

        if (columnIndex === -1) {
            setError("Failed to find column index");
            return;
        }

        // set the mapping
        setMapping((prev) => ({ ...prev, [key]: columnIndex }));
    };

    useEffect(() => {
        /**
         * Fetches the import mapping data and sets the mapping state.
         *
         * This function attempts to fetch the import mapping data from a remote source using `getImportMapping`.
         * If the data is available, it constructs a new mapping object by iterating over the `headerMappingConfig`
         * and finding the corresponding index from the fetched data. The new mapping is then set using `setMapping`.
         *
         * If the fetched data is not available, it attempts to prefill the mapping using the parsed CSV data.
         * It constructs a new mapping object by iterating over the `headerMappingConfig` and finding the index
         * of the header in the CSV headers. The comparison is done in a case-insensitive manner and ignores whitespace.
         * The new mapping is then set using `setMapping`.
         *
         * @returns {Promise<void>} A promise that resolves when the mapping has been set.
         */
        const fetchMapping = async () => {
            const data = await getImportMapping();

            // if we have a mapping from supa, use that
            if (data) {
                const newMapping: Partial<Record<keyof ImportFilePayload, number>> = {};
                // loop through the mapping config and try to find the index of the header
                headerMappingConfig.forEach((field, index) => {
                    // find the index of the header
                    const mappingIndex = data.mapping_data[index];
                    if (mappingIndex !== -1) {
                        newMapping[field.key] = mappingIndex;
                    }
                });

                // set that ish
                setMapping(newMapping);

                // we have the mapping, no need to prefill
                return;
            }

            // if we have a parsed csv, try to prefill the mapping
            if (parsedCsv) {
                const newMapping: Partial<Record<keyof ImportFilePayload, number>> = {};
                // loop through the mapping config and try to find the index of the header
                headerMappingConfig.forEach((field) => {
                    // find the index of the header. Dumbed down to lowercase and no whitespace
                    const index = csvHeaders.findIndex(
                        (header) =>
                            header.toLowerCase().replace(/\s/g, "") ===
                            field.displayLabel.toLowerCase().replace(/\s/g, ""),
                    );
                    if (index !== -1) {
                        newMapping[field.key] = index;
                    }
                });
                setMapping(newMapping);
            }
        };

        fetchMapping();
    }, [parsedCsv]);

    return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 ">
            <div className="w-full overflow-y-auto max-h-[300px] px-6">
                {/* Headers */}
                <div className="grid grid-cols-[5fr_1fr_5fr] gap-2 items-center">
                    <div className="text-gray-600 text-sm flex items-center">HappyDoc Data</div>
                    <div className="w-4" />
                    <div className="text-gray-600 text-sm">Your Data</div>
                </div>
                <div className="mt-4">
                    {headerMappingConfig.map((field) => (
                        <div key={field.key} className="grid grid-cols-[5fr_1fr_5fr] gap-2 items-center py-4">
                            {/* Left col */}
                            <div className="w-full flex flex-row items-center justify-start gap-1">
                                {field.required && (
                                    <span className="flex-shrink-0">
                                        {" "}
                                        <span className="text-max-900">*</span>
                                    </span>
                                )}
                                <div className="w-full bg-gray-100 rounded-md border border-gray-300 px-2 py-1 sm:px-3 sm:py-2  text-start text-gray-500 text-xs sm:text-sm shadow-sm">
                                    {field.displayLabel}
                                </div>
                            </div>
                            {/* middle */}
                            <div className="flex items-center justify-center">
                                <ArrowRightIcon className="h-4 h-4 sm:h-5 sm:w-5 text-gray-600" />
                            </div>
                            {/* Right col */}
                            <div>
                                <HDSingleSelect
                                    options={csvHeaders}
                                    value={
                                        mapping[field.key] !== undefined
                                            ? csvHeaders[mapping[field.key as keyof ImportFilePayload]!]
                                            : null
                                    }
                                    onChange={(value) => handleMappingChange(field.key, value)}
                                    placeholder="Make Selection"
                                    customClassNames="w-full"
                                    useSearch={true}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {error && (
                <div
                    className={classNames(
                        "flex w-full py-3 mt-2 px-2 items-center justify-center gap-4 bg-red-50",
                        "border-l-[6px] border-t-[1px] border-b-[1px] border-r-0 border-red-600",
                    )}
                >
                    <span className={classNames("text-xs font-medium ", "text-red-900")}>{error}</span>
                </div>
            )}
        </div>
    );
};

export default Matcher;
